import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Pagination, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Typeahead, Menu, MenuItem, Highlighter, Loader } from 'react-bootstrap-typeahead';
import { addMonths, format, isAfter, lastDayOfMonth, startOfMonth, sub } from 'date-fns';
import { DateFilter } from './components';
import TableComponent from '../commons/components/TableComponent';
import './assets/styles/CompanyKPIContainer.scss';
import IconArrowGround from './assets/images/icons/arrow-ground.svg';
import { getDateFromString, isPartialMatch } from '../../utils/helpers';
import { filterType } from '../../constants/filterOptions';
import {
  GetFreemiumCorporateList,
  GetPaidCorporateList,
  downloadKPIDumpData,
  getKPIDumpData,
  saveCropIdNameMap,
  saveFreemiumCropIdNameMap,
} from './store/actions';
import { toast } from 'react-toastify';
import { Placeholder, useConfirm } from '../commons/components';
import ArrowBack from './assets/images/icons/arrow-back';

const viewType = {
  groupByUserId: 'groupByUserId',
  noGroup: 'noGroup',
};

const KPIDumpContainer = () => {
  const DEFAULT_RANGE_MONTHS = 3;
  const now = new Date();
  const startDateDate = startOfMonth(addMonths(new Date(), -DEFAULT_RANGE_MONTHS));
  const endDateDate = sub(now, { days: 1 });
  const [startValue, setStartValue] = useState(startDateDate);
  const [endValue, setEndValue] = useState(endDateDate);
  const kpiDataDump = useSelector((state) => state.kpi.kpiDataDump);
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [currentView, setCurrentView] = useState(filterType.custom);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [companySelected, setCompanySelected] = useState([]);
  const [copyCorporateList, setCopyCorporateList] = useState([]);

  const selectedCompany = useSelector((state) => state.kpi.selectedCompany);
  const corporateList = useSelector((state) => state.kpi.corporateList);
  const freemiumCorporateList = useSelector((state) => state.kpi.freemiumCorporateList);

  const params = useParams();
  const { info } = useConfirm();
  const auth = useSelector((state) => state.auth);

  const [page, setPage] = useState(1);
  const [selectedView, setSelectedView] = useState(viewType.groupByUserId);

  useEffect(() => {
    fetchData(1);
    setPage(1);
  }, [startValue, endValue, companySelected, params, selectedView]);

  function fetchData(pageNo) {
    if (startValue && endValue && companySelected.length > 0) {
      dispatch(
        getKPIDumpData({
          params,
          pageNo,
          companyIdList: companySelected.map((c) => c._id),
          startDate: format(startValue, 'yyyy-MM-dd'),
          endDate: format(endValue, 'yyyy-MM-dd'),
          groupByUserId: selectedView === viewType.groupByUserId && !params.id ? true : false,
        })
      );
    }
  }

  function fetchDumpDataGroupByUserId(k) {
    setSelectedView(k);
  }

  useEffect(() => {
    if (selectedCompany && selectedCompany.length === 1) {
      setCompanySelected(selectedCompany);
      navigate(`/kpi-data-dump`);
    }
  }, [selectedCompany]);

  useEffect(() => {
    dispatch(GetPaidCorporateList({ viewedFrom: 'kpi' }));
    //if (!freemiumCorporateList.data?.length) {
    dispatch(GetFreemiumCorporateList({ viewedFrom: 'kpi' }));
    //}
  }, []);

  useEffect(() => {
    if (
      corporateList &&
      Array.isArray(corporateList.data) &&
      corporateList.data.length >= 1 &&
      companySelected.length === 0
    ) {
      let tmpCorporateList = [...corporateList.data];
      const ownCompany = corporateList.data.find((a) => a._id === auth.data.corporateId);
      if (ownCompany) {
        // If company owner is Wellness Coach, then show All Paid Company as company to get summary of all companies
        if (ownCompany.entity_name === 'Wellness Coach') {
          if (!selectedCompany) {
            setCompanySelected([{ _id: 'ALL', entity_name: 'All Paid Company', status: 1 }]);
          }
          tmpCorporateList.unshift({ _id: 'ALL', entity_name: 'All Paid Company', status: 1 });
        } else {
          setCompanySelected([ownCompany]);
        }
      } else {
        setCompanySelected(corporateList.data.slice(0, 1));
      }
      setCopyCorporateList(tmpCorporateList);
      const corpIdNameMap = {};
      tmpCorporateList.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveCropIdNameMap(corpIdNameMap));
    }
  }, [corporateList]);

  useEffect(() => {
    if (
      freemiumCorporateList &&
      Array.isArray(freemiumCorporateList.data) &&
      freemiumCorporateList.data.length >= 1
    ) {
      const corpIdNameMap = {};
      freemiumCorporateList.data.forEach((c) => {
        corpIdNameMap[c._id] = c.entity_name;
      });
      dispatch(saveFreemiumCropIdNameMap(corpIdNameMap));
    }
  }, [freemiumCorporateList]);

  function getMonthsInRange(startDate, endDate) {
    const months = [];
    let currentDate = new Date(startDate);
    let currentEndDate = new Date(endDate);
    while (
      isAfter(currentEndDate, currentDate) ||
      currentDate.getMonth() === currentEndDate.getMonth()
    ) {
      months.push(format(lastDayOfMonth(currentDate), 'yyyy-MM-dd'));
      currentDate = addMonths(currentDate, 1);
    }
    return months;
  }

  const monthsInRange = getMonthsInRange(startValue, endValue);

  const columns = useMemo(
    () => [
      {
        Header: 'User ID',
        accessor: 'userId',
        clickable: true,
        disableSortBy: true,
        hidden: params.id ? true : false,
      },
      {
        Header: 'User Name',
        accessor: 'userName',
        clickable: false,
        disableSortBy: true,
        hidden: params.id ? true : false,
      },
      {
        Header: 'User Email',
        accessor: 'userEmail',
        clickable: false,
        disableSortBy: true,
        hidden: params.id ? true : false,
      },
      {
        Header: selectedView === viewType.groupByUserId ? 'Last Active Date' : 'Active Date',
        accessor: 'activeDate',
        disableSortBy: true,
      },
      {
        Header: 'Enterprise Name',
        accessor: 'vendorName',
        disableSortBy: true,
        hidden: companySelected?.[0]?._id === 'ALL' ? false : true,
      },
      {
        Header: 'Total Steps',
        accessor: 'todaysSteps',
        disableSortBy: true,
      },
      {
        Header: 'Total Minutes',
        accessor: 'todaysMins',
        disableSortBy: true,
      },
      {
        Header: 'Total Calories Burned',
        accessor: 'todaysCaloriesBurned',
        disableSortBy: true,
      },
      {
        Header: 'Total Workout Minutes',
        accessor: 'todaysWorkoutMins',
        disableSortBy: true,
      },
      {
        Header: 'Water Intake',
        accessor: 'waterIntake',
        disableSortBy: true,
      },
      {
        Header: 'PC Bookings',
        accessor: 'pcBookings',
        disableSortBy: true,
      },
      {
        Header: 'Ratings Given',
        accessor: 'ratingsGiven',
        disableSortBy: true,
      },
      {
        Header: 'Challenge Participated',
        accessor: 'challengeParticipated',
        disableSortBy: true,
      },
      {
        Header: 'Reward Points Earned',
        accessor: 'rewardPointsEarned',
        disableSortBy: true,
      },
      {
        Header: 'Health Sync',
        accessor: 'healthSync',
        disableSortBy: true,
      },
      {
        Header: 'App Type (Locale)',
        accessor: 'appType',
        disableSortBy: true,
      },
      {
        Header: 'App Version',
        accessor: 'appVersion',
        disableSortBy: true,
      },
      {
        Header: 'Timezone',
        accessor: 'timezone',
        disableSortBy: true,
      },
    ],
    [kpiDataDump.loading, monthsInRange]
  );

  const data = useMemo(() => {
    if ((kpiDataDump.data?.list || []).length === 0) {
      const placeholderRows = Array.from({ length: 20 }, (_, index) => ({
        userId: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        userName: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        userEmail: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        activeDate: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        vendorName: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        todaysSteps: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        todaysMins: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        todaysCaloriesBurned: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        todaysWorkoutMins: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        waterIntake: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        pcBookings: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        ratingsGiven: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        challengeParticipated: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        rewardPointsEarned: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        healthSync: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        appType: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        appVersion: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
        timezone: kpiDataDump.loading ? <Placeholder width='80%' height={20} /> : '',
      }));
      return placeholderRows;
    }
    return (kpiDataDump.data?.list || []).map((kpiData) => {
      const data = {
        userId: kpiData.user_id,
        userName: kpiDataDump.data?.userNamesMap?.[kpiData.user_id]?.name || '',
        userEmail: kpiDataDump.data?.userNamesMap?.[kpiData.user_id]?.email || '',
        activeDate: format(new Date(kpiData.active_date), 'dd MMM, yyyy'),
        vendorName: kpiData.corporate_name,
        todaysSteps: kpiData.todays_steps ? parseInt(kpiData.todays_steps).toLocaleString() : 0,
        todaysMins: kpiData.todays_mins ? parseInt(kpiData.todays_mins).toLocaleString() : 0,
        todaysCaloriesBurned: kpiData.todays_calories_burned ? parseInt(kpiData.todays_calories_burned).toLocaleString() : 0,
        todaysWorkoutMins: kpiData.todays_workout_minutes ? parseInt(kpiData.todays_workout_minutes).toLocaleString() : 0,
        waterIntake: kpiData.todays_water_intake || 0,
        pcBookings: kpiData.personal_coaching_bookings || 0,
        challengeParticipated: kpiData.total_challenges_participated || 0,
        ratingsGiven: kpiData.total_ratings_given || 0,
        rewardPointsEarned: kpiData.reward_points_earned || 0,
        healthSync: kpiData.health_data_sync ? (
          <span className='text-success'>Yes</span>
        ) : (
          <span className='text-danger'>No</span>
        ),
        appType: `${kpiData.app_type || 'N/A'} (${kpiData.locale || 'N/A'})`,
        appVersion: kpiData.app_version,
        timezone: kpiData.timezone,
      };

      return data;
    });
  }, [kpiDataDump, monthsInRange]);

  const onClickDateFilter = (e) => {
    e.stopPropagation();
    setShowDateFilter(!showDateFilter);
  };

  const onFilterUpdate = (selectedFilter, sDate, eDate) => {
    setStartValue(sDate);
    setEndValue(eDate);
    setCurrentView(selectedFilter.value);
  };

  const onClick = (cell) => {
    if (cell.column.id === 'userId') {
      navigate(`/kpi-data-dump/${cell.value}`, {
        state: {
          startDate: format(startValue, 'yyyy-MM-dd'),
          endDate: format(endValue, 'yyyy-MM-dd'),
        },
      });
    }
  };

  async function downloadDump() {
    let startDate = format(new Date(startValue), 'yyyy-MM-dd');
    let endDate = format(new Date(endValue), 'yyyy-MM-dd');

    const response = await dispatch(
      downloadKPIDumpData({
        companyIdList: companySelected.map((c) => c._id),
        startDate: startDate,
        endDate: endDate,
        email: auth.data.email,
        viewedFrom: 'kpi',
        params
      })
    );
    if (response.payload && response.payload.status === 200) {
      await info(
        'Your request has been received! You will receive an email shortly with the report.'
      );
    } else {
      toast.error('Something went wrong! Please try again.', {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
      });
    }
  }

  function getCorporateLogo(companySelected) {
    if (!companySelected[0]) {
      return '';
    }
    if (!companySelected[0]?.company_logo) {
      return (
        <div className='company-report--header--logo--no-logo-just-first-letter'>
          {companySelected[0].entity_name.charAt(0)}
        </div>
      );
    }
    return companySelected && companySelected[0]?.company_logo ? (
      <img src={companySelected[0].company_logo} alt='' />
    ) : (
      ''
    );
  }

  return (
    <div className='container-fluid kpi kpi-dump'>
      <div className='company-report--actions'>
        <div className='company-report--header'>
          <div className='company-report--header--logo'>{getCorporateLogo(companySelected)}</div>
          <div className='company-report--header--title'>
            <Typeahead
              id='some_id'
              onChange={(selected) => {
                setCompanySelected(selected);
                navigate(`/kpi-data-dump`);
              }}
              renderMenu={(results, menuProps) => {
                let listWithActive = !copyCorporateList
                  ? []
                  : copyCorporateList.filter((c) => c.status === 1);

                const results_len = results.length;
                return (
                  <Menu {...menuProps} emptyLabel='No results found'>
                    {results.map((result, index) => (
                      <MenuItem option={result} position={index} key={index}>
                        <Highlighter search={menuProps.text}>{result.entity_name}</Highlighter>
                      </MenuItem>
                    ))}
                    {results_len > 0 && <Menu.Divider />}
                    {listWithActive.slice(0, 30).map(
                      (result, index) =>
                        result?._id !== companySelected[0]?._id && (
                          <MenuItem
                            option={result}
                            position={index + results_len + 1}
                            key={index + results_len + 1}
                          >
                            {result.entity_name}
                          </MenuItem>
                        )
                    )}
                    {listWithActive.length > 30 ? 'Please type name to see more ... ' : ''}
                  </Menu>
                );
              }}
              className={
                copyCorporateList && copyCorporateList.length === 1
                  ? 'company-report--header--single'
                  : ''
              }
              filterBy={['entity_name']}
              labelKey={'entity_name'}
              options={copyCorporateList ? copyCorporateList.filter((c) => c.status === 1) : []}
              selected={companySelected}
            />
          </div>
        </div>

        <div className='d-flex align-items-center gap-3'>
          <div className='company-report--actions--date'>
            <div className='company-report--actions--date--wrapper' onClick={onClickDateFilter}>
              <Badge className='current-filter' bg='light' text='dark'>
                {currentView || 'Custom'}
              </Badge>
              {startValue && endValue && (
                <span className='date-range'>
                  {format(getDateFromString(startValue), 'MMM d')} -{' '}
                  {format(getDateFromString(endValue), 'MMM d, yyyy')}
                </span>
              )}
              <span className='select-icon-down' aria-hidden='true'></span>
            </div>
            {showDateFilter && (
              <DateFilter
                onClose={() => setShowDateFilter(false)}
                startValue={startValue}
                endValue={endValue}
                onFilterUpdate={onFilterUpdate}
              />
            )}
          </div>
          <div className={`company-report--actions--download`} onClick={downloadDump}>
            <img src={IconArrowGround} alt='' />
            <span>Download</span>
          </div>
        </div>
      </div>
      <div className='d-flex justify-content-between align-items-center'>
        <div className='d-flex align-items-center gap-1 ms-2'>
          <ArrowBack onClick={() => navigate(-1)} />
          <div>
            <h4 className='mb-0'>Showing Results for {companySelected[0]?.entity_name}</h4>
            {params.id ? (
              <div>
                User: <span>{`${kpiDataDump?.data?.userNamesMap?.[params.id]?.name || ''}`}</span>{' '}
                {`(${kpiDataDump?.data?.userNamesMap?.[params.id]?.email || ''} ) - (${params.id
                  })` || ''}
              </div>
            ) : (
              ''
            )}
          </div>
        </div>

        <Pagination size='sm'>
          <Pagination.Prev
            onClick={() => {
              setPage(page - 1);
              fetchData(page - 1);
            }}
            disabled={page === 1 || kpiDataDump?.loading}
          >
            &larr; Previous
          </Pagination.Prev>
          <Pagination.Item disabled>Page {page}</Pagination.Item>
          <Pagination.Next
            onClick={() => {
              setPage(page + 1);
              fetchData(page + 1);
            }}
            disabled={kpiDataDump?.data?.list?.length < 100 || kpiDataDump?.loading}
          >
            Next &rarr;
          </Pagination.Next>
        </Pagination>
        <div>&nbsp;</div>
      </div>
      {/* {!params.id && (
        <Tabs
          defaultActiveKey={selectedView}
          id='uncontrolled-tab-example'
          className='mb-3'
          onSelect={(k) => fetchDumpDataGroupByUserId(k)}
        >
          <Tab eventKey={viewType.groupByUserId} title='Grouped Users'></Tab>
          <Tab eventKey={viewType.noGroup} title='Individual Users'></Tab>
        </Tabs>
      )} */}
      <div id='data-table-container' className='data-table-container table-responsive mt-3'>
        {!kpiDataDump.loading && kpiDataDump.data?.list?.length === 0 ? (
          <div className='no-data-container'>Data Not Available</div>
        ) : null}
        <TableComponent columns={columns} data={data} onClick={onClick} />
      </div>
    </div>
  );
};

export default KPIDumpContainer;
